'use client'

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconDownload, IconFileImport, IconLoader2 } from '@tabler/icons-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { MutedText } from '@/components/ui/typography'
import { useAuth } from '@/hooks/useAuth'
import {
  exportStudyDeckAnkiTsv,
  exportStudyDeckCsv,
  importStudyCsvToDeck
} from '@/lib/study'
import { parseStudyCsv, STUDY_CSV_TEMPLATE } from '@/lib/study-card-generation'

type Props = {
  deckId: string
  deckName: string
}

function downloadText(filename: string, text: string, type: string) {
  const blob = new Blob([text], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function safeFilename(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function DeckImportExportPanel({ deckId, deckName }: Props) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [csvText, setCsvText] = useState('')
  const preview = useMemo(() => parseStudyCsv(csvText), [csvText])
  const validCount = preview.filter(
    (row) => row.input && row.errors.length === 0
  ).length
  const errorRows = preview.filter((row) => row.errors.length > 0)

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Sign in to import cards')
      return importStudyCsvToDeck(user.id, deckId, csvText)
    },
    onSuccess: (result) => {
      toast.success(`Imported ${result.imported} cards`)
      setCsvText('')
      queryClient.invalidateQueries({ queryKey: ['study-deck-items', deckId] })
      queryClient.invalidateQueries({ queryKey: ['all-collections'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Import failed')
    }
  })

  const exportCsvMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Sign in to export cards')
      return exportStudyDeckCsv(deckId, user.id)
    },
    onSuccess: (text) => {
      downloadText(`${safeFilename(deckName)}.csv`, text, 'text/csv')
    }
  })

  const exportAnkiMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Sign in to export cards')
      return exportStudyDeckAnkiTsv(deckId, user.id)
    },
    onSuccess: (text) => {
      downloadText(
        `${safeFilename(deckName)}-anki.tsv`,
        text,
        'text/tab-separated-values'
      )
    }
  })

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
      <div className="rounded-lg border p-4">
        <div className="mb-3">
          <h3 className="text-sm font-semibold">Import CSV</h3>
          <MutedText className="text-sm">
            Paste rows, preview them, then import only the valid cards.
          </MutedText>
        </div>
        <Textarea
          value={csvText}
          onChange={(event) => setCsvText(event.target.value)}
          rows={12}
          spellCheck={false}
          placeholder={STUDY_CSV_TEMPLATE}
          className="font-mono text-xs"
        />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <MutedText className="text-sm">
            {preview.length
              ? `${validCount} valid, ${errorRows.length} need attention`
              : 'Use the template format shown in the placeholder.'}
          </MutedText>
          <Button
            disabled={!validCount || importMutation.isPending}
            onClick={() => importMutation.mutate()}
          >
            {importMutation.isPending ? (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <IconFileImport className="mr-2 h-4 w-4" />
            )}
            Import
          </Button>
        </div>
        {errorRows.length > 0 && (
          <div className="border-destructive/30 bg-destructive/5 text-destructive mt-3 rounded-md border p-3 text-sm">
            {errorRows.slice(0, 5).map((row) => (
              <div key={row.rowNumber}>
                Row {row.rowNumber}: {row.errors.join(', ')}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4">
        <h3 className="text-sm font-semibold">Export</h3>
        <MutedText className="mt-1 text-sm">
          Save a copy of this deck or move it into Anki with a TSV import.
        </MutedText>
        <div className="mt-4 grid gap-2">
          <Button
            variant="outline"
            className="justify-start"
            disabled={exportCsvMutation.isPending}
            onClick={() => exportCsvMutation.mutate()}
          >
            <IconDownload className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            disabled={exportAnkiMutation.isPending}
            onClick={() => exportAnkiMutation.mutate()}
          >
            <IconDownload className="mr-2 h-4 w-4" />
            Export for Anki
          </Button>
        </div>
        <div className="bg-muted/40 mt-4 rounded-md p-3 font-mono text-xs whitespace-pre-wrap">
          {STUDY_CSV_TEMPLATE.split('\n').slice(0, 2).join('\n')}
        </div>
      </div>
    </div>
  )
}
